import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { Material } from "@/api/schemas";
import type { Zoradenie } from "@/components/cennik/cennikZalozky";
import { formatDate, formatEur, toNum } from "@/lib/format";

/** „+4,2 %" / „−3,1 %" — percento zo servera, po slovensky s typografickým mínusom. */
export function formatZmenaCeny(pct: number): string {
  const text = Math.abs(pct).toLocaleString("sk-SK", {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });
  return `${pct > 0 ? "+" : "\u2212"}${text}\u00a0%`;
}

/**
 * Štítok zmeny nákupnej ceny pri cene v Cenníku (KOLO 166).
 *
 * Percento aj okno 90 dní rozhoduje server (`services/zmena_ceny`) —
 * `zmena_ceny_pct` príde len pri čerstvej zmene, takže štítok aj triedenie
 * „Najviac zdražené" ukazujú to isté. Predošlú cenu a dátum zapisuje
 * pri každej zmene ceny listener v backende (import, úprava, % úprava).
 */
export function ZmenaCenyZnak({ material }: { material: Material }): JSX.Element | null {
  // Bez zmeny v okne príde `null` (→ NaN). Zmena pod 0,05 % sa zaokrúhli
  // na nulu — „+0,0 %" by nič nepovedalo.
  const pct = toNum(material.zmena_ceny_pct);
  if (!Number.isFinite(pct) || pct === 0) return null;
  const zdrazenie = pct > 0;
  const title =
    `Predtým ${formatEur(material.predosla_cena)}` +
    (material.cena_zmenena_at ? ` · zmena ${formatDate(material.cena_zmenena_at)}` : "");
  return (
    <span
      className={
        "rounded-full px-1.5 py-0 text-[10px] font-medium " +
        (zdrazenie
          ? "bg-destructive/10 text-destructive"
          : "bg-success/10 text-success")
      }
      title={title}
    >
      {formatZmenaCeny(pct)}
    </span>
  );
}

/** Triedenie podľa zmeny ceny — kľúč pre rozbaľovačku nad tabuľkou. */
const ZMENY_CIEN = {
  zdrazene: { pole: "zmena_ceny", smer: "desc", label: "Najviac zdražené" },
  zlacnene: { pole: "zmena_ceny", smer: "asc", label: "Najviac zlacnené" },
  zmenene: { pole: "zmenene", smer: "desc", label: "Naposledy zmenené" },
} as const satisfies Record<string, Zoradenie & { label: string }>;

type ZmenaCienKluc = keyof typeof ZMENY_CIEN;
const BEZ = "__bez__";

/**
 * Rozbaľovačka „Zmeny cien" nad tabuľkou (KOLO 166) — triedenie, ktoré
 * nemá vlastný stĺpec. Je to to isté `zoradit` ako klik na hlavičku,
 * takže hlavička ho zruší a naopak.
 */
export function ZoraditPodlaZmeny({
  zoradenie,
  onZmena,
}: {
  zoradenie: Zoradenie | null;
  onZmena: (z: Zoradenie | null) => void;
}): JSX.Element {
  const aktualny =
    (Object.keys(ZMENY_CIEN) as ZmenaCienKluc[]).find(
      (k) =>
        ZMENY_CIEN[k].pole === zoradenie?.pole &&
        ZMENY_CIEN[k].smer === zoradenie?.smer,
    ) ?? BEZ;
  return (
    <Select
      value={aktualny}
      onValueChange={(v) => {
        if (v === BEZ) {
          onZmena(null);
          return;
        }
        const { pole, smer } = ZMENY_CIEN[v as ZmenaCienKluc];
        onZmena({ pole, smer });
      }}
    >
      <SelectTrigger
        // Tlačidlo v riadku filtrov ako ostatné (KOLO 199)
        className={
          "h-9 w-auto shrink-0 gap-1.5 rounded-full px-3 text-sm font-medium" +
          (aktualny !== BEZ ? " border-primary bg-tandem-red-light text-tandem-red-dark" : "")
        }
        aria-label="Zoradiť podľa zmeny ceny"
        title="Zmeny nákupných cien za posledných 90 dní"
      >
        {/* Ako ostatné tlačidlá filtrov: názov, pri výbere aj hodnota */}
        <SelectValue>
          {aktualny === BEZ ? "Zmeny cien" : `Zmeny cien: ${ZMENY_CIEN[aktualny].label.toLowerCase()}`}
        </SelectValue>
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={BEZ}>Bez triedenia podľa zmeny</SelectItem>
        {(Object.keys(ZMENY_CIEN) as ZmenaCienKluc[]).map((k) => (
          <SelectItem key={k} value={k}>
            {ZMENY_CIEN[k].label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
